import {
  ManyIncludedProductResourceWithFakeRelationshipUnion,
  SingleIncludedProductResourceWithFakeRelationshipUnion,
} from 'api-client'

import { ProductFilter } from '~shared-components/filters/products.filters'
import {
  FilterMap,
  mapFiltersToAPIQuery,
} from '~shared-components/filters/utils'
import { APIProduct, Product } from '~shared-components/model'
import { transformAPIProductToProduct } from '~shared-components/transforms/product.transforms'
import { deserialize } from '~shared-components/utils/deserialize'

import { request } from './request'

export const fetchProduct = async (
  apiBaseUrl: string,
  customerContextId: string,
  productId: string,
  salesGroupId?: string,
  shipToLocationId?: string,
): Promise<Product | undefined> => {
  const response = await request<SingleIncludedProductResourceWithFakeRelationshipUnion>(
    {
      method: 'GET',
      params: {
        'filter[customerContextProducts.customerContext.id.equals]': customerContextId,
        'filter[pricePoint.ship_to.id.equals]': shipToLocationId,
        'filter[salesGroupProducts.salesGroup.id.equals]': salesGroupId,
        include: 'pricePoints,customerContextProducts,salesGroupProducts',
      },
      url: `${apiBaseUrl}/api/products/${productId}`,
    },
  )
  if (response?.data === undefined) return

  return transformAPIProductToProduct(deserialize<APIProduct>(response.data))
}

/**
 *  Any transforms that need to be done to the filter query
 *  before it gets passed along to the API should go here
 */
const formatFilters = (filters: FilterMap<typeof ProductFilter>) => filters

interface Params {
  apiBaseUrl: string
  customerContextId?: string
  filters: FilterMap<typeof ProductFilter>
  include?: string
  limit: number | undefined
  offset: number
  salesGroupId?: string
  shipToLocationId?: string
}

export const fetchProducts = async ({
  apiBaseUrl,
  customerContextId,
  filters,
  include,
  limit,
  offset,
  salesGroupId,
  shipToLocationId,
}: Params): Promise<{
  error?: boolean
  packagingTypes?: Record<string, string>
  products: Product[] | undefined
  total: number
}> => {
  try {
    const response = await request<ManyIncludedProductResourceWithFakeRelationshipUnion>(
      {
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'GET',
        params: Object.assign(
          {
            'filter[customerContextProducts.customerContext.id.equals]': customerContextId,
            'filter[pricePoint.ship_to.id.equals]': shipToLocationId,
            'filter[salesGroupProducts.salesGroup.id.equals]': salesGroupId,
            include: include
              ? include
              : 'pricePoints,customerContextProducts,salesGroupProducts',
            'page[number]': offset,
            'page[size]': limit,
          },
          mapFiltersToAPIQuery(formatFilters(filters), ProductFilter),
        ),
        url: `${apiBaseUrl}/api/products`,
      },
    )

    if (response) {
      const products = deserialize<Product[]>(response.data)

      return {
        packagingTypes: response.data?.meta?.aggregations?.['packaging-types'],
        products: products.map(transformAPIProductToProduct),
        total: response.data?.meta?.count || 0,
      }
    } else {
      return {
        error: true,
        products: undefined,
        total: 0,
      }
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log(error)

    return {
      error: true,
      products: undefined,
      total: 0,
    }
  }
}

export const fetchAltSizes = async (
  apiBaseUrl: string,
  customerContextId: string,
  baseProductId: string,
  salesGroupId?: string,
  shipToLocationId?: string,
): Promise<{
  error?: boolean
  products: Product[] | undefined
  total: number
}> => {
  try {
    const response = await request<ManyIncludedProductResourceWithFakeRelationshipUnion>(
      {
        method: 'GET',
        params: {
          'filter[baseProduct.id.equals]': baseProductId,
          'filter[customerContextProducts.customerContext.id.equals]': customerContextId,
          'filter[pricePoint.ship_to.id.equals]': shipToLocationId,
          'filter[salesGroupProducts.salesGroup.id.equals]': salesGroupId,
          include: 'pricePoints,customerContextProducts,salesGroupProducts',
        },
        url: `${apiBaseUrl}/api/products`,
      },
    )

    if (!response) {
      return {
        error: true,
        products: undefined,
        total: 0,
      }
    }

    const products = deserialize<APIProduct[]>(response.data)

    return {
      products: products
        .filter(i => i.id !== baseProductId)
        .map(transformAPIProductToProduct)
        .sort((a, b) => a.packaging.size.quantity - b.packaging.size.quantity),
      total: response.data?.meta?.count || 0,
    }
  } catch {
    return {
      error: true,
      products: undefined,
      total: 0,
    }
  }
}
